import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './index.css';
// 新增：引入获取当前用户信息的函数
import { getCurrentUser } from '../../data/postsData';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  // 新增：用户弹窗相关状态
  const [showUserMenu, setShowUserMenu] = useState(false);
  // 新增：主题状态（true=深色，false=浅色）
  const [isDarkMode, setIsDarkMode] = useState(false);
  const userMenuRef = useRef(null); // 用于点击外部关闭弹窗
  const navigate = useNavigate();
  const location = useLocation();

  // 新增：初始化主题状态
  useEffect(() => {
    // 读取localStorage或系统偏好
    const savedTheme = localStorage.getItem('theme');
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const initialIsDark = savedTheme ? savedTheme === 'dark' : prefersDark;
    
    setIsDarkMode(initialIsDark);
    // 同步到根元素class
    if (initialIsDark) {
      document.documentElement.classList.add('dark-mode');
    }
  }, []);

  // 修复：用useCallback包裹toggleTheme，保证函数引用稳定
  const toggleTheme = useCallback(() => {
    const newIsDark = !isDarkMode;
    setIsDarkMode(newIsDark);
    
    // 1. 更新根元素class
    if (newIsDark) {
      document.documentElement.classList.add('dark-mode');
    } else {
      document.documentElement.classList.remove('dark-mode');
    }
    
    // 2. 保存到localStorage
    localStorage.setItem('theme', newIsDark ? 'dark' : 'light');
    
    // 3. 友好提示（可选）
    alert(newIsDark ? '已切换到深色模式 🕶️' : '已切换到浅色模式 ☀️');
  }, [isDarkMode]);

  // 新增：获取当前登录用户信息
  const currentUser = getCurrentUser();
  
  // 替换原有硬编码的 isLoggedIn，从 localStorage 获取真实登录状态
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    return localStorage.getItem('isLoggedIn') === 'true';
  });

  const navItems = [
    { path: '/', label: '首页', icon: '🏠', desc: '回到温暖小窝' },
    { path: '/write', label: '写文章', icon: '✏️', desc: '记录生活点滴' },
    { path: '/archive', label: '归档', icon: '📚', desc: '浏览历史文章' },
    { path: '/about', label: '关于', icon: '🐶', desc: '个人中心' }, // 调整文案：从「了解博客故事」改为「个人中心」
  ];

  // 监听滚动：控制header样式
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 监听点击外部关闭用户弹窗
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 判断导航项是否激活
  const isActive = (path) => location.pathname === path;

  // 搜索功能
  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      navigate(`/?search=${encodeURIComponent(searchInput.trim())}`);
      setShowSearch(false);
      setSearchInput('');
    }
  };

  // 快捷搜索
  const handleQuickSearch = (term) => {
    navigate(`/?search=${encodeURIComponent(term)}`);
    setShowSearch(false);
  };

  // 键盘快捷键：按 / 键聚焦搜索框
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === '/' && e.target.tagName !== 'INPUT') {
        e.preventDefault();
        setShowSearch(true);
        setTimeout(() => {
          document.querySelector('.search-input')?.focus();
        }, 100);
      }
      if (e.key === 'Escape' && (showSearch || showUserMenu)) {
        setShowSearch(false);
        setShowUserMenu(false);
      }
      // 新增：按 T 键快速切换主题
      if (e.key === 't' && e.target.tagName !== 'INPUT') {
        e.preventDefault();
        toggleTheme();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showSearch, showUserMenu, toggleTheme]); // 修复：加入toggleTheme到依赖数组

  // 处理用户菜单点击事件
  const handleUserMenuClick = (action) => {
    setShowUserMenu(false);
    switch (action) {
      case 'login':
        navigate('/auth/login'); // 跳转到登录页
        break;
      case 'logout':
        // 修复：完善退出登录逻辑
        localStorage.removeItem('isLoggedIn');
        localStorage.removeItem('currentUserId');
        setIsLoggedIn(false);
        alert('已退出登录 🐾');
        navigate('/auth'); // 跳转到登录页
        break;
      case 'switchAccount':
        // 修复：切换账号跳转到统一的auth页面，而非404路径
        navigate('/auth');
        break;
      default:
        break;
    }
  };

  return (
    <header className={`header ${scrolled ? 'scrolled' : ''}`}>
      <div className="header-container">
        {/* Logo */}
        <div className="logo" onClick={() => navigate('/')}>
          <span className="logo-icon">🐕</span>
          <div className="logo-text">
            <h1 className="logo-title">Puppy Log</h1>
            <p className="logo-subtitle">温暖的小狗日志</p>
          </div>
        </div>

        {/* 导航菜单 - 核心导航项在这里 */}
        <nav className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-link ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => setIsMenuOpen(false)}
              title={item.desc}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-label">{item.label}</span>
              {isActive(item.path) && <span className="active-indicator">🐾</span>}
            </Link>
          ))}
        </nav>

        {/* 右侧操作区 */}
        <div className="header-actions">
          <button 
            className="btn btn-secondary search-toggle-btn"
            onClick={() => setShowSearch(!showSearch)}
            title="搜索文章 (快捷键: /)"
          >
            🔍 搜索
          </button>
          <button 
            className="btn btn-primary write-btn"
            onClick={() => navigate('/write')}
          >
            ✏️ 写文章
          </button>
          
          {/* 新增：主题切换按钮 */}
          <button
            className="theme-toggle-btn"
            onClick={toggleTheme}
            title={`切换到${isDarkMode ? '浅色' : '深色'}模式 (快捷键: T)`}
          >
            {isDarkMode ? '☀️' : '🌙'}
          </button>
          
          {/* 核心修改：用户头像 + 下拉弹窗 */}
          <div className="user-avatar-wrapper" ref={userMenuRef}>
            <button 
              className="user-avatar-btn"
              onClick={() => setShowUserMenu(!showUserMenu)}
              aria-label="用户菜单"
            >
              <span className="avatar-icon">{isLoggedIn ? currentUser.avatar || '🐶' : '👤'}</span>
              {/* 下拉箭头 */}
              <span className={`avatar-arrow ${showUserMenu ? 'active' : ''}`}>▼</span>
            </button>
            
            {/* 用户下拉菜单 */}
            {showUserMenu && (
              <div className="user-dropdown-menu">
                {isLoggedIn ? (
                  <>
                    {/* 已登录状态：显示退出、切换账号 */}
                    <div className="user-menu-header">
                      <span className="menu-avatar">{currentUser.avatar || '🐶'}</span>
                      <div className="menu-user-info">
                        {/* 修复：使用真实用户昵称，而非硬编码 */}
                        <div className="menu-username">{currentUser.name}</div>
                        <div className="menu-user-desc">{currentUser.intro || '温暖的小狗日志作者'}</div>
                      </div>
                    </div>
                    <div className="menu-divider"></div>
                    <button 
                      className="menu-item"
                      onClick={() => handleUserMenuClick('switchAccount')}
                    >
                      <span className="menu-icon">🔄</span>
                      <span className="menu-label">切换账号</span>
                    </button>
                    <button 
                      className="menu-item logout-item"
                      onClick={() => handleUserMenuClick('logout')}
                    >
                      <span className="menu-icon">🚪</span>
                      <span className="menu-label">退出登录</span>
                    </button>
                  </>
                ) : (
                  // 未登录状态：显示登录
                  <button 
                    className="menu-item login-item"
                    onClick={() => handleUserMenuClick('login')}
                  >
                    <span className="menu-icon">🔑</span>
                    <span className="menu-label">登录 / 注册</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 搜索下拉框（保留原有逻辑） */}
        {showSearch && (
          <div className="search-overlay">
            <div className="search-modal" onClick={(e) => e.stopPropagation()}>
              <form onSubmit={handleSearch} className="header-search-form">
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="搜索文章标题、内容、标签或作者..."
                  className="search-input"
                  autoFocus
                />
                <button type="submit" className="search-submit-btn">
                  🔍
                </button>
                <button 
                  type="button" 
                  className="search-close-btn"
                  onClick={() => setShowSearch(false)}
                >
                  ✕
                </button>
              </form>
              <div className="search-suggestions">
                <div className="suggestion-title">热门搜索：</div>
                <div className="suggestion-tags">
                  {['React', '小狗', '生活', '旅行', '技术', '读书'].map((tag) => (
                    <button
                      key={tag}
                      className="suggestion-tag"
                      onClick={() => handleQuickSearch(tag)}
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <div className="search-backdrop" onClick={() => setShowSearch(false)} />
          </div>
        )}

        {/* 移动端菜单按钮（保留） */}
        <button 
          className="menu-toggle"
          onClick={() => setIsMenuOpen(!isMenuOpen)}
          aria-label="菜单"
        >
          <span className={`toggle-line ${isMenuOpen ? 'open' : ''}`}></span>
          <span className={`toggle-line ${isMenuOpen ? 'open' : ''}`}></span>
          <span className={`toggle-line ${isMenuOpen ? 'open' : ''}`}></span>
        </button>
      </div>
    </header>
  );
};

export default Header;